import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import DiffView from "@/components/DiffView";
import StatBox from "@/components/StatBox";
import { toast } from "sonner";
import { ArrowLeft, Radio, Clock, Users, Trophy, Send } from "lucide-react";

function fmtMMSS(secs) {
  const s = Math.max(0, Math.floor(secs));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}
function fmtHMS(secs) {
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return h > 0 ? `${h}h ${m}m ${r}s` : `${m}m ${r}s`;
}
function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function LiveTestDetail() {
  const { id } = useParams();
  const [t, setT] = useState(undefined);  // undefined=loading, null=not-found
  const [board, setBoard] = useState(null);
  const [typed, setTyped] = useState("");
  const [start, setStart] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const timerRef = useRef(null);
  const autoRef = useRef(false);

  const load = useCallback(() => {
    api.get(`/live-tests/${id}`).then((r) => setT(r.data)).catch(() => setT(null));
    api.get(`/live-tests/${id}/leaderboard`).then((r) => setBoard(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    timerRef.current = setInterval(() => setTick((x) => x + 1), 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  // Every 5s reload if state can flip (upcoming→live or live→ended)
  useEffect(() => {
    if (tick % 5 === 0) load();
  }, [tick, load]);

  const register = async () => {
    setBusy(true);
    try {
      await api.post(`/live-tests/${id}/register`);
      toast.success("Registered! Test is scheduled — aa jana on time.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not register");
    } finally { setBusy(false); }
  };

  const submit = useCallback(async (opts = {}) => {
    if (busy || result) return;
    const auto = !!opts.auto;
    if (!auto && !typed.trim()) { toast.error("Type something first"); return; }
    setBusy(true);
    try {
      const seconds = start ? (Date.now() - start) / 1000 : (t?.duration_seconds || 900);
      const { data } = await api.post(`/live-tests/${id}/submit`, { typed_text: typed || "-", time_seconds: seconds });
      setResult(data);
      toast.success(auto ? `Time up! Auto-submitted — ${data.accuracy_percent}% accuracy` : `Submitted — ${data.accuracy_percent}% accuracy`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  }, [busy, result, typed, start, id, t, load]);

  // Client-computed remaining seconds (source of truth for auto-submit and timer display)
  const clientRemaining = t?.scheduled_at
    ? Math.max(0, Math.floor((new Date(t.scheduled_at).getTime() + (t.duration_seconds || 900) * 1000 - Date.now()) / 1000))
    : 0;
  const clientToStart = t?.scheduled_at
    ? Math.max(0, Math.floor((new Date(t.scheduled_at).getTime() - Date.now()) / 1000))
    : 0;
  const isLiveNow = t?.scheduled_at
    ? (Date.now() >= new Date(t.scheduled_at).getTime() &&
       Date.now() < new Date(t.scheduled_at).getTime() + (t.duration_seconds || 900) * 1000)
    : false;

  const endTs = t?.scheduled_at
    ? new Date(t.scheduled_at).getTime() + (t?.duration_seconds || 900) * 1000
    : 0;

  // Auto-submit on time-out — driven by CLIENT clock, not backend polling.
  // Only fires if the window just closed within the last 10s (grace window),
  // so opening an ancient ended test doesn't trigger a doomed 400.
  useEffect(() => {
    if (!t || result) return;
    const justClosed = endTs > 0 && Date.now() - endTs >= 0 && Date.now() - endTs <= 10_000;
    if (t.is_registered && !t.has_submitted && justClosed && typed.trim() && !autoRef.current) {
      autoRef.current = true;
      submit({ auto: true });
    }
  }, [t, result, endTs, typed, submit]);

  if (t === undefined) return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-12 font-mono text-muted-foreground" data-testid="live-test-loading">Loading…</main>
    </div>
  );
  if (t === null) return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-12" data-testid="live-test-not-found">
        <h2 className="text-3xl font-black tracking-tight mb-3">Live test not found</h2>
        <p className="font-mono text-muted-foreground mb-6">This test may have been removed or is not yet published.</p>
        <Link to="/live-tests" className="text-brand-crimson font-bold underline underline-offset-4">← Back to live tests</Link>
      </main>
    </div>
  );

  const original = t.exercise?.text || t.exercise?.content || "";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-8">
        <Link to="/live-tests" className="text-xs uppercase tracking-[0.3em] font-bold text-muted-foreground hover:text-brand-crimson" data-testid="live-test-back">
          <ArrowLeft className="w-3.5 h-3.5 inline mr-1" /> Back to Live tests
        </Link>

        <div className="mt-4 mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-crimson flex items-center gap-2">
              <Radio className="w-3 h-3" /> {t.module.toUpperCase()} · Live test
            </div>
            <h1 className="text-3xl lg:text-4xl font-black tracking-tight mt-2" data-testid="live-test-title">{t.title}</h1>
            {t.description && <p className="mt-2 font-mono text-muted-foreground max-w-2xl">{t.description}</p>}
          </div>
          {t.state === "upcoming" && !isLiveNow && (
            <div className="border-2 border-brand-gold/40 px-4 py-2 text-brand-gold flex items-center gap-2 font-mono font-bold text-lg" data-testid="live-test-countdown">
              <Clock className="w-5 h-5" /> Starts in {fmtHMS(clientToStart)}
            </div>
          )}
          {isLiveNow && !result && (
            <div className="border-2 border-red-600 bg-red-600 text-white px-4 py-2 flex items-center gap-2 font-mono font-bold text-xl" data-testid="live-test-timer">
              <Clock className="w-5 h-5" /> {fmtMMSS(clientRemaining)}
            </div>
          )}
          {t.state === "ended" && !isLiveNow && (
            <div className="border-2 border-neutral-300 px-4 py-2 text-muted-foreground flex items-center gap-2 font-mono font-bold text-lg">
              Test ended
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-6 font-mono text-sm">
          <div className="border border-border p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Scheduled</div>
            <div>{fmtDate(t.scheduled_at)}</div>
          </div>
          <div className="border border-border p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Duration</div>
            <div>{Math.round(t.duration_seconds / 60)} minutes</div>
          </div>
          <div className="border border-border p-4 flex items-center gap-2">
            <Users className="w-4 h-4" /> <span data-testid="live-test-count">{t.registered_count}</span> registered
          </div>
        </div>

        {/* Actions per state */}
        {t.state === "upcoming" && !t.is_registered && !isLiveNow && (
          <Button onClick={register} disabled={busy} data-testid="live-test-register-btn" className="rounded-lg h-12 px-8 bg-brand-crimson hover:bg-red-700 text-white text-xs uppercase tracking-[0.2em] font-bold">
            {busy ? "Registering…" : "Register — free"}
          </Button>
        )}
        {t.state === "upcoming" && t.is_registered && !isLiveNow && (
          <div className="border-2 border-green-500 bg-green-50 p-4 font-mono text-sm text-green-800" data-testid="live-test-registered">
            ✔ You're registered. Aa jana <b>{fmtDate(t.scheduled_at)}</b> — test start hote hi neeche typing box appear ho jayega.
          </div>
        )}
        {isLiveNow && !t.is_registered && (
          <div className="border-2 border-red-600 bg-red-50 p-4 font-mono text-sm text-red-700">
            Registration closed — test start ho chuka. Next weekly test ke liye register karo.
          </div>
        )}

        {/* Test window */}
        {isLiveNow && t.is_registered && !result && !t.has_submitted && t.exercise && (
          <section className="mt-6 grid md:grid-cols-2 border border-border">
            <div className="p-4 border-b md:border-b-0 md:border-r border-border bg-secondary/60 max-h-[70vh] overflow-y-auto">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Source</div>
              {t.exercise.images?.length ? (
                <div className="space-y-3">
                  {t.exercise.images.map((u, i) => (
                    <img key={u} src={`${process.env.REACT_APP_BACKEND_URL}${u}`} alt={`p${i + 1}`} className="w-full border border-border" />
                  ))}
                </div>
              ) : t.exercise.audio_url ? (
                <>
                  <audio controls src={`${process.env.REACT_APP_BACKEND_URL}${t.exercise.audio_url}`} className="w-full mb-3" />
                  <div className="font-mono text-xs text-muted-foreground">Audio play karke transcription type karo</div>
                </>
              ) : (
                <div className="font-mono text-sm text-foreground whitespace-pre-wrap" data-testid="live-test-source">{original}</div>
              )}
            </div>
            <div className="p-4 flex flex-col">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Your typing</div>
              <Textarea
                value={typed}
                onChange={(e) => { setTyped(e.target.value); if (!start) setStart(Date.now()); }}
                disabled={busy}
                placeholder="Start typing…"
                className="flex-1 min-h-[300px] rounded-lg border-2 border-brand-gold/30 bg-white text-[#0F172A] placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:border-brand-gold font-mono font-bold text-base leading-loose"
                data-testid="live-test-textarea"
              />
              <div className="mt-2 flex justify-between text-[10px] font-mono text-muted-foreground">
                <span>{typed.trim().split(/\s+/).filter(Boolean).length} words</span>
                <span>Auto-submit {fmtMMSS(clientRemaining)} me</span>
              </div>
              <Button onClick={() => submit()} disabled={busy} data-testid="live-test-submit-btn" className="mt-4 rounded-lg h-12 bg-brand-gold hover:bg-brand-gold/90 text-white text-xs uppercase tracking-[0.2em] font-bold">
                <Send className="w-4 h-4 mr-2" /> {busy ? "Submitting…" : "Submit now"}
              </Button>
            </div>
          </section>
        )}

        {t.has_submitted && !result && (
          <div className="mt-6 border-2 border-green-500 bg-green-50 p-4 font-mono text-sm text-green-800" data-testid="live-test-already-submitted">
            ✔ You have already submitted this test. Scroll down for leaderboard.
          </div>
        )}

        {/* Result */}
        {result && (
          <section className="mt-8" data-testid="live-test-result">
            <h2 className="text-2xl font-black tracking-tight mb-4">Your result</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatBox label="Accuracy" value={result.accuracy_percent} unit="%" tone={result.accuracy_percent >= 90 ? "good" : "warn"} testId="live-test-result-accuracy" />
              <StatBox label="WPM" value={result.wpm} unit="wpm" tone="accent" />
              <StatBox label="Correct" value={`${result.correct}/${result.total_words}`} tone="default" />
              <StatBox label="Errors" value={result.error_percent} unit="%" tone="bad" />
            </div>
            <DiffView result={result.diff} />
          </section>
        )}

        {/* Leaderboard */}
        <section className="mt-10">
          <h2 className="text-2xl font-black tracking-tight flex items-center gap-2 mb-4">
            <Trophy className="w-6 h-6 text-brand-crimson" /> Leaderboard <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold ml-2">Top 25</span>
          </h2>
          {board?.rows?.length ? (
            <div className="border border-border divide-y divide-black/10" data-testid="live-test-leaderboard">
              {board.rows.map((r) => (
                <div key={r.user_id + r.rank} className={`p-3 flex items-center gap-4 ${r.is_me ? "bg-brand-blue/10" : ""}`} data-testid={`live-test-leaderboard-row-${r.rank}`}>
                  <div className={`w-9 h-9 flex items-center justify-center font-black ${r.rank === 1 ? "bg-yellow-400 text-black" : r.rank === 2 ? "bg-neutral-300 text-black" : r.rank === 3 ? "bg-amber-600 text-white" : "border-2 border-border"}`}>
                    {r.rank}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{r.name} {r.is_me && <span className="text-[10px] uppercase tracking-widest text-brand-gold">You</span>}</div>
                    <div className="text-xs font-mono text-muted-foreground">{fmtDate(r.submitted_at)}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-black">{r.accuracy_percent}%</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{r.wpm} wpm</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="border-2 border-dashed border-border p-8 text-center font-mono text-sm text-muted-foreground">
              No submissions yet — leaderboard tab bharega jab log submit karenge.
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
